import { Effect } from 'effect';
import { type ComponentType } from 'react';
import { lazyWithPreloadRecovery } from '@/lib/chunk-recovery';

export interface ToolRunnerProps {
  slug: string;
}

const lazyRunner = lazyWithPreloadRecovery<ToolRunnerProps>;

/**
 * Slug -> runner component. Add an entry here when porting a legacy tool into
 * the current web app. Tools without an entry render a "coming soon" placeholder.
 */
const RUNNERS: Record<string, ComponentType<ToolRunnerProps>> = {
  merge: lazyRunner(() => import('./merge').then((m) => ({ default: m.MergeRunner }))),
  split: lazyRunner(() => import('./split').then((m) => ({ default: m.SplitRunner }))),
  rotate: lazyRunner(() => import('./rotate').then((m) => ({ default: m.RotateRunner }))),
  compress: lazyRunner(() => import('./compress').then((m) => ({ default: m.CompressRunner }))),
  metadata: lazyRunner(() => import('./metadata').then((m) => ({ default: m.MetadataRunner }))),
  organize: lazyRunner(() => import('./organize').then((m) => ({ default: m.OrganizeRunner }))),
  watermark: lazyRunner(() => import('./watermark').then((m) => ({ default: m.WatermarkRunner }))),
  'page-numbers': lazyRunner(() =>
    import('./page-numbers').then((m) => ({ default: m.PageNumbersRunner })),
  ),
  bates: lazyRunner(() => import('./bates').then((m) => ({ default: m.BatesRunner }))),
  'jpg-to-pdf': lazyRunner(() =>
    import('./jpg-to-pdf').then((m) => ({ default: m.JpgToPdfRunner })),
  ),
  'png-to-pdf': lazyRunner(() =>
    import('./png-to-pdf').then((m) => ({ default: m.PngToPdfRunner })),
  ),
  'pdf-to-jpg': lazyRunner(() =>
    import('./pdf-to-jpg').then((m) => ({ default: m.PdfToJpgRunner })),
  ),
  'pdf-to-png': lazyRunner(() =>
    import('./pdf-to-png').then((m) => ({ default: m.PdfToPngRunner })),
  ),
  flatten: lazyRunner(() => import('./flatten').then((m) => ({ default: m.FlattenRunner }))),
  compare: lazyRunner(() => import('./compare').then((m) => ({ default: m.CompareRunner }))),
  crop: lazyRunner(() => import('./crop').then((m) => ({ default: m.CropRunner }))),
  highlight: lazyRunner(() => import('./highlight').then((m) => ({ default: m.HighlightRunner }))),
  redact: lazyRunner(() => import('./redact').then((m) => ({ default: m.RedactRunner }))),
  sign: lazyRunner(() => import('./sign').then((m) => ({ default: m.SignRunner }))),
  ocr: lazyRunner(() => import('./ocr').then((m) => ({ default: m.OcrRunner }))),
  'word-to-pdf': lazyRunner(() =>
    import('./word-to-pdf').then((m) => ({ default: m.WordToPdfRunner })),
  ),
  'excel-to-pdf': lazyRunner(() =>
    import('./excel-to-pdf').then((m) => ({ default: m.ExcelToPdfRunner })),
  ),
  'pdf-to-word': lazyRunner(() =>
    import('./pdf-to-word').then((m) => ({ default: m.PdfToWordRunner })),
  ),
  'pdf-to-excel': lazyRunner(() =>
    import('./pdf-to-excel').then((m) => ({ default: m.PdfToExcelRunner })),
  ),
  'pdf-to-markdown': lazyRunner(() =>
    import('./pdf-to-markdown').then((m) => ({ default: m.PdfToMarkdownRunner })),
  ),
  'pdf-to-json': lazyRunner(() =>
    import('./pdf-to-json').then((m) => ({ default: m.PdfToJsonRunner })),
  ),
  'pdf-to-html': lazyRunner(() =>
    import('./pdf-to-html').then((m) => ({ default: m.PdfToHtmlRunner })),
  ),
  'handwriting-ocr': lazyRunner(() =>
    import('./handwriting-ocr').then((m) => ({ default: m.HandwritingOcrRunner })),
  ),
  'pdf-to-link': lazyRunner(() =>
    import('./pdf-to-link').then((m) => ({ default: m.PdfToLinkRunner })),
  ),
  'verapdf-checker': lazyRunner(() =>
    import('./pdf-accessibility-checker').then((m) => ({ default: m.VeraPdfCheckerRunner })),
  ),
  'matterhorn-pdf-checker': lazyRunner(() =>
    import('./pdf-accessibility-checker').then((m) => ({ default: m.MatterhornPdfCheckerRunner })),
  ),
  'well-tagged-pdf-checker': lazyRunner(() =>
    import('./pdf-accessibility-checker').then((m) => ({ default: m.WellTaggedPdfCheckerRunner })),
  ),
};

export const getToolRunnerEffect = Effect.fn('web.toolRunner.resolve')(function* (slug: string) {
  return RUNNERS[slug] ?? null;
});

export function getToolRunner(slug: string): ComponentType<ToolRunnerProps> | null {
  return Effect.runSync(getToolRunnerEffect(slug));
}
